/**
 * 
 * ??Are we currently using this /#greenreport
 * 
 */

import React, { useState } from 'react';
import { Button, ButtonGroup, Col, Container, Form, FormGroup, Input, Label, Row } from 'reactstrap';

import KStatus from '../base/data/KStatus';
import { getDataLogData, pivotDataLogData } from '../base/plumbing/DataLog';
import DataStore from '../base/plumbing/DataStore';
import C from '../C';
import ActionMan from '../plumbing/ActionMan';

import Misc from '../base/components/Misc';
import SimpleTable from '../base/components/SimpleTable';
import printer from '../base/utils/printer';

const totalRowNames = {
	domain: 'All Domains',
	country: 'All Countries',
	os: 'All Systems',
	device: 'All Devices',
};


function Bytes({b, ...props}) {
	const exp = Math.floor(Math.max(0, Math.log10(b))); // exponent for the number (hack so 0 bytes gets same treatment as 1 byte)
	const unitIndex = Math.min(Math.floor(exp / 3), 4); // Unit limited to TB --> divisor limited to 10^12
	let unit = ['bytes', 'KB', 'MB', 'GB', 'TB'][unitIndex];
	const divisor = Math.pow(10, unitIndex * 3);
	const num = printer.prettyNumber(b / divisor, 3); // so 999 bytes, 1.0 KB
	if (b === 1) unit = 'byte'; // correct plural in the one case it's relevant
	return <span title={`${b} bytes`} {...props}>{num ? `${num} ${unit}` : num}</span>;
}


function TagWeight({tag}) {
  return <div>
		{tag.weight ? (
			<Bytes b={tag.weight} />
		) : <>
			<span className="text-danger">Size unset </span>
			<a href={`#green/${tag.id}/edit`}>(edit)</a>
		</>}
	</div>;
}


function TagSummary({tag}) {
	return <Col sm="2" className="tag-summary">
		Name: <strong>{tag.name}</strong> (<a href={`#greenreport/tag/${tag.id}`}>view report</a>)
		<TagWeight tag={tag} />
	</Col>;
}


function CampaignList() {
	let campaigns = DataStore.fetch(['widget', 'greenreport', 'campaignlist'], () => {
		return ActionMan.list({
			type: C.TYPES.GreenTag,
			status: KStatus.PUBLISHED,
		}).promise.then(res => {
			return Object.keys(
				Object.values(res.hits).reduce((acc, tag) => {
					acc[tag.campaign] = true;
					return acc;
				}, {})
			);
		});
	}).value;
	
	if (!campaigns) return <Misc.Loading text="Fetching campaign list..." />;

	return <>
		<h3>All green ad tag campaigns:</h3>
		{campaigns.map(cmp => (
			<div><a href={`#greenreport/campaign/${cmp}`}>{cmp}</a></div>
		))}
	</>;
}


function CampaignTable({ id }) {
	if (!id) return <CampaignList />;

	// Display impression counts or bytes transferred?
	const [showBytes, setShowBytes] = useState(false);
	// How many bytes of data does it take to render an ad - regardless of creative size?
	const [overhead, setOverhead] = useState(100000);
	// What should the column breakdown be?
	const [bbyCol, setBbyCol] = useState('adid');
	// What should the row breakdown be?
	const [bbyRow, setBbyRow] = useState('time');
	// Filter the list of tags?
	const [filterTerm, setFilterTerm] = useState('');
	const [filterExclude, setFilterExclude] = useState(false);
	// You want a report but you don't have time or data to set weight on all the tags?
	const [overrideAvg, setOverrideAvg] = useState(0);

	// Get a list of green tags for the specified campaign
	let tags = ActionMan.list({
		type: C.TYPES.GreenTag,
		status: KStatus.PUBLISHED,
		q: `campaign:${id}`
	});

	if (!tags.value) return <Misc.Loading text="Please wait, fetching tags for this campaign..." />;
	tags = tags.value.hits;

	// Apply the tag filter?
	if (filterTerm) tags = tags.filter(tag => filterExclude === !tag.name.match(filterTerm));

	// Map IDs to tags, for convenience later
	const tagsById = tags.reduce((obj, tag) => ({...obj, [tag.id]: tag}), {});

	// How far back should reporting go?
	const startDate = tags.reduce((earliest, tag) => {
		const thisCreatedDate = new Date(tag.created);
		return (thisCreatedDate < earliest) ? thisCreatedDate : earliest;
	}, new Date());
	startDate.setHours(0, 0, 0, 0); // bump back to start of day

	// Fetch cross-breakdown for all green tags in this campaign
	const impressionData = DataStore.fetch(['widget', 'greendash', 'tables', 'campaign', id, `by${bbyRow}`, `by${bbyCol}`], () => {
		return getDataLogData({
			dataspace: 'green',
			q: `evt:pixel AND campaign:${id}`,
			breakdowns: [`${bbyRow}/${bbyCol}`],
			start: startDate.toISOString(),
		}).promise.then(res => {
			// pivot to table format...
			const rows = Object.entries(pivotDataLogData(res, [bbyRow, bbyCol])).map(([rowName, row]) => ({[bbyRow]: rowName, ...row}));
			const totalRow = { [bbyRow]: (totalRowNames[bbyRow] || 'All') }; // and extract all-row totals for every column
			rows.forEach(row => {
				// HACK Look for fixable bad tag IDs generated by SSP-side URL component escaping problems e.g. "biFrCxIU;bcsi-ac-eebe329824..."
				// ...and assign their impressions to the "correct" tag ID, e.g. "biFrCxIU"
				const badKeys = Object.keys(row).filter(key => key !== bbyRow && !tagsById[key]);
				badKeys.forEach(badkey => {
					// Is there an initial alphanumeric string? Is it a tag ID?
					const [tagid] = badkey.match(/^\w+/) || [];
					if (!tagid || !tagsById[tagid]) return;
					if (!row[tagid]) row[tagid] = 0;
					row[tagid] += row[badkey];
					delete row[badkey];
				});
				Object.entries(row).forEach(([tagid, count]) => {
					if (tagid === bbyRow || !count) return;
					if (!totalRow[tagid]) totalRow[tagid] = 0;
					totalRow[tagid] += count; // Increment total for this tag
				});
			});
			return { totalRow, rows };
		});
	}).value;

	if (!impressionData) return <Misc.Loading text="Please wait, fetching impression counts..." />;

	// Weighted-by-impressions average of tag byte sizes - for fill-in purposes
	// First, how many impressions do we know the byte weights for?
	const impsKnownBytes = Object.entries(impressionData.totalRow).reduce((acc, [tagid, imps]) => {
		if (!tagsById[tagid] || !tagsById[tagid].weight) return acc;
		return acc + imps;
	}, 0);
	// Then, the weighted average
	let avgBytes = Object.entries(impressionData.totalRow).reduce((acc, [tagid, imps]) => {
		if (!tagsById[tagid] || !tagsById[tagid].weight) return acc;
		return acc + (tagsById[tagid].weight * (imps / impsKnownBytes));
	}, 0);
	// User can say "actually that's wrong" or shim in an estimated size when no tags have weight
	if (overrideAvg) avgBytes = overrideAvg;

	// Convert impressions to bytes if in data mode, just retrieve impressions if not
	const cellValueFn = showBytes ? (
		(row, key) => (row[key] || 0) * (((bbyCol === 'adid' && tagsById[key].weight) || avgBytes) + overhead)
	) : (
		(row, key) => (row[key] || 0)
	);

	// Breakdown by date? First column should convert epoch to readable date
	const firstCol = (bbyRow === 'time') ? {
		Header: 'Date',
		accessor: row => (row[bbyRow] === 'All') ? 'All Dates' : new Date(parseInt(row[bbyRow])).toLocaleDateString()
	} : bbyRow;

	// Col header: raw or retrieve from mapping?
	const colHeaderFn = colName => (bbyCol === 'adid') ? tagsById[colName].name : colName;
	
	// HACK - when in tag-column mode, take column names from the filtered tags list instead of the aggregate row.
	const colKeys = bbyCol === 'adid' ? (
		Object.keys(tagsById)
	) : (
		Object.keys(impressionData.totalRow).filter(key => key !== bbyRow)
	);

	// Use the retrieved green tag objects to label the columns
	// Construct a total column with a reduce operation on row
	// Multiply impression counts by relevant tag weight if set, by weighted avg otherwise
	// Default to zero impressions so empty cells sort correctly
	const restCols = [
		{
			Header: 'Total',
			accessor: row => Object.entries(row).reduce((count, [colname, imps]) => {
				if (!tagsById[colname]) return count; // Ignore any column that isn't a tag ID
				return count + cellValueFn(row, colname);
			}, 0),
		},
		...colKeys.map(colName => ({
			Header: colHeaderFn(colName),
			accessor: row => cellValueFn(row, colName)
		}))
	];

	// Are we displaying data usage? Add nice formatting to show bytes/KB/MB/etc
	if (showBytes) restCols.forEach(col => (col.Cell = num => <Bytes b={num} />));

	/* TODO Green tag names make long column labels - tilt them?
		.green-metrics th {
			position: relative;
		}
		.green-metrics th div {
			position: absolute;
			white-space: nowrap;
			transform-origin: bottom left;
			transform: rotate(-45deg);
		}
	*/

	// Are there any tags which don't have a creative weight set?
	const missingBytes = tags.find(tag => !tag.weight);
	// Are there actually NO tags with creative weight set?
	const noBytesSet = tags.reduce((acc, tag) => acc && !tag.weight, true);

	// Warn the user if some or all tags are missing creative weight.
	let unsetSizeWarning;
	if (showBytes && noBytesSet) {
		unsetSizeWarning = <div>
			None of the tags in this report have a creative size set. If you want to get approximate data weights anyway, you can enter a provisional size here.
			<Form inline className="mb-2">
				<FormGroup>
					<Label for="overrideAvg" className="mr-1">Est. average size</Label>
					<Input type="number" name="overrideAvg" step="1" onChange={e => setOverrideAvg(e.target.valueAsNumber || 0)} value={overrideAvg} />
					<Bytes b={overrideAvg} className="mx-1"/> per impression
				</FormGroup>
			</Form>
		</div>;
	} else if (showBytes && missingBytes) {
		unsetSizeWarning = <div>Some of the Green Tags in this report do not have a creative size set. For the purposes of this table, their impressions have been assigned a size averaged from the others (<Bytes b={avgBytes} />).</div>;
	}

	return <>
		<h2>Green Ad Tags: Report on campaign {id}</h2>
		<Container className="tags-list">
			<Row><Col xs="12"><h3>Tags for this campaign</h3></Col></Row>
			<Row className="mb-2">
				<Col xs="12">
					<Form inline className="mb-2">
						<FormGroup>
							<Label className="mr-1" for="term">Filter?</Label>
							<Input className="mr-1" type="text" name="term" value={filterTerm} onChange={e => setFilterTerm(e.target.value)} />
						</FormGroup>
						<FormGroup check>
							<Input type="checkbox" name="exclude" checked={filterExclude} onChange={e => setFilterExclude(e.target.checked)}></Input>
							<Label for="exclude">Exclude</Label>
						</FormGroup>
					</Form>
					{filterTerm && <div>Filter: {filterExclude ? 'Exclude' : 'Include only'} tags with term "{filterTerm}" in the name</div>}
				</Col>
			</Row>
			<Row className="mb-2">{ tags.map(tag => <TagSummary tag={tag} key={tag.id} />) }</Row>
		</Container>

		<h3>{showBytes ? 'Data' : 'Impressions'} by {bbyRow} vs { bbyCol === 'adid' ? 'tag' : bbyCol}</h3>

		<Form inline className="mb-2">
			<ButtonGroup>
				<Button type="primary" disabled={!showBytes} onClick={() => setShowBytes(false)}>Impressions</Button>
				<Button type="primary" disabled={showBytes} onClick={() => setShowBytes(true)}>Data Transferred</Button>
			</ButtonGroup>
			{showBytes ? (
				<FormGroup className="ml-2">
					<Label for="overhead" className="mr-1">Serving overhead</Label>
					<Input type="number" value={overhead} onChange={e => setOverhead(e.target.valueAsNumber || 0)} />
					<Bytes className="mx-1" b={overhead} /> per impression
				</FormGroup>
			) : null}
		</Form>

		{unsetSizeWarning}
		
		<Form inline className="mb-2">
			<FormGroup>
				<Label for="bbyCol" className="mr-1">Column breakdown:</Label>
				<Input type="select" name="bbyCol" value={bbyCol} onChange={e => setBbyCol(e.target.value)}>
					<option value="adid">Green Tag</option>
					<option value="mbl">Mobile/Desktop</option>
					<option value="country">Country</option>
					<option value="os">Operating system</option>
				</Input>
			</FormGroup>
			<FormGroup className="ml-2">
				<Label for="bbyRow" className="mr-1">Row breakdown:</Label>
				<Input type="select" name="bbyRow" value={bbyRow} onChange={e => setBbyRow(e.target.value)}>
					<option value="time">Time</option>
					<option value="country">Country</option>
					<option value="domain">Domain</option>
					<option value="os">Operating system</option>
				</Input>
			</FormGroup>
		</Form>

		<SimpleTable data={impressionData.rows} columns={[firstCol, ...restCols]} rowsPerPage={20} topRow={impressionData.totalRow} hasCsv />
	</>;
}


function TagTable({ id }) {
	if (!id) {
		return <div>
			No tag ID given. Try the <a href="#greenreport/campaign">campaigns section</a> to find what you want to report on.
		</div>;
	}

	const tag = ActionMan.getDataItem({ type: C.TYPES.GreenTag, status: KStatus.PUBLISHED, id}).value;
	if (!tag) return <Misc.Loading text="Fetching info for selected Green Ad Tag..." />;

	const [bbyRow, setBbyRow] = useState('time');
	const [bbyCol, setBbyCol] = useState('null'); // null as string because it has to be selectable from a dropdown
	const xbd = (bbyCol !== 'null');

	const [overrideWeight, setOverrideWeight] = useState(0);

	// Display impression counts or bytes transferred?
	const [showBytes, setShowBytes] = useState(false);
	// How many bytes of data does it take to render an ad - regardless of creative size?
	const [overhead, setOverhead] = useState(100000);

	// fetch path might have "bynull" as last element - this is fine & prevents path collision between single- and cross-breakdown
	const impressionData = DataStore.fetch(['widget', 'greendash', 'tables', 'tag', id, `by${bbyRow}`, `by${bbyCol}`], () => {
		return getDataLogData({
			dataspace: 'green',
			q: `evt:pixel AND adid:${id}`,
			breakdowns: xbd ? [`${bbyRow}/${bbyCol}`] : [bbyRow],
			start: tag.created,
		}).promise.then(res => {
			const pivotBy = xbd ? [bbyRow, bbyCol] : [bbyRow]; // Single or cross-breakdown pivot?
			const rows = Object.entries(pivotDataLogData(res, pivotBy)).map(([rowname, row]) => {
				return xbd ? {[bbyRow]: rowname, ...row} : {[bbyRow]: rowname, count: row};
			});

			const totalRow = { [bbyRow]: (totalRowNames[bbyRow] || 'All') }; // and extract all-row totals for every column
			rows.forEach(row => {
				Object.entries(row).forEach(([tagid, count]) => {
					if (tagid === bbyRow || !count) return;
					if (!totalRow[tagid]) totalRow[tagid] = 0;
					totalRow[tagid] += count; // Increment total for this tag
				});
			});
			return { totalRow, rows };
		});
	}).value;

	if (!impressionData) return <Misc.Loading text={`Fetching impression data for tag "${tag.name}"`} />;

	// Convert impressions to bytes (if in data mode)
	const cellValueFn = showBytes ? (
		(row, key) => (row[key] || 0) * ((tag.weight || overrideWeight) + overhead)
	) : (
		(row, key) => (row[key] || 0)
	);

	// Breakdown by date? First column should convert epoch to readable date
	let firstCol = (bbyRow === 'time') ? {
		Header: 'Date',
		accessor: row => (row[bbyRow] === 'All') ? 'All Dates' : new Date(parseInt(row[bbyRow])).toLocaleDateString()
	} : bbyRow;

	// Cross-breakdown or just one count column?
	let restCols = xbd ? Object.keys(impressionData.totalRow).filter(key => key !== bbyRow).map(colName => ({
		Header: colName,
		accessor: row => cellValueFn(row, colName)
	})) : [{Header: showBytes ? 'Data' : 'Impressions', accessor: row => cellValueFn(row, 'count')}];

	// Add a "Total for all columns" column if it's warranted
	if (bbyCol && restCols.length > 1) {
		restCols.unshift({
			Header: 'Total',
			accessor: row => Object.keys(row).filter(k => k !== bbyRow).reduce((acc, key) => acc + cellValueFn(row, key), 0)
		});
	}

	// Are we displaying data usage? Add nice formatting to show bytes/KB/MB/etc
	if (showBytes) restCols.forEach(col => col.Cell = num => <Bytes b={num} />);

	return <>
		<h2>Green Ad Tag: Report on tag "{tag.name}"</h2>
		<div>
			Campaign: <strong>{tag.campaign}</strong> (<a href={`#greenreport/campaign/${tag.campaign}`}>view report</a>)<br/>
			<TagWeight tag={tag} />
		</div>
		{showBytes && !tag.weight &&	<div>
			Creative size unset: <a href={`#greentag/${tag.id}/edit`}>edit</a> or set a provisional value here<br/>
			<Form inline className="mb-2">
				<FormGroup>
					<Input type="number" name="overrideWeight" value={overrideWeight} onChange={e => setOverrideWeight(e.target.valueAsNumber || 0)} />
					<Label for="overrideWeight"><Bytes b={overrideWeight} className="mx-1"/> per impression</Label>
				</FormGroup>
			</Form>
		</div>}
		<Form inline className="mb-2">
			<ButtonGroup>
				<Button type="primary" disabled={!showBytes} onClick={() => setShowBytes(false)}>Impressions</Button>
				<Button type="primary" disabled={showBytes} onClick={() => setShowBytes(true)}>Data Transferred</Button>
			</ButtonGroup>
			{showBytes ? (
				<FormGroup className="ml-2">
					<Label for="overhead" className="mr-1">Serving overhead</Label>
					<Input type="number" name="overhead" value={overhead} onChange={e => setOverhead(e.target.valueAsNumber || 0)} />
					<Bytes className="mx-1" b={overhead} /> per impression
				</FormGroup>
			) : null}
		</Form>
		<Form inline className="mb-2">
			<FormGroup>
				<Label for="bbyCol" className="mr-1">Column breakdown:</Label>
				<Input type="select" name="bbyCol" value={bbyCol} onChange={e => setBbyCol(e.target.value)}>
					<option value="null">None</option>
					<option value="mbl">Mobile/Desktop</option>
					<option value="country">Country</option>
					<option value="os">Operating system</option>
				</Input>
			</FormGroup>
			<FormGroup className="ml-2">
				<Label for="bbyRow" className="mr-1">Row breakdown:</Label>
				<Input type="select" name="bbyRow" value={bbyRow} onChange={e => setBbyRow(e.target.value)}>
					<option value="time">Time</option>
					<option value="domain">Domain</option>
				</Input>
			</FormGroup>
		</Form>
		<SimpleTable data={impressionData.rows} topRow={impressionData.totalRow} columns={[firstCol, ...restCols]} />
	</>;
}

/**
 * Provide single-tag and whole-campaign tables for Green Ad Tags.
 * Shows impression counts & data-transfer totals with various single and cross breakdowns.
 * TODO Campaign and tag mode ended up almost identical - abstract them into a single component.
 * @param {} param0 
 * @returns 
 */
function GreenTagReportPage({}) {
	const path = DataStore.getValue(['location', 'path']);
	const tableType = path[1]; // campaign or tag
	const itemId = path[2];

	const Table = {
		campaign: CampaignTable,
		tag: TagTable,
	}[tableType] || (() => 'Invalid mode: ' + tableType);

	return <div className="green-subpage green-metrics">
		<Container>
			<Row>
				<Col md="12">
					<Table id={itemId} />
				</Col>
			</Row>
		</Container>
	</div>;
}


export default GreenTagReportPage;
